import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    FormTemplateBreadcrumbs,
    FormTemplateCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FormTemplateActions from "../../../actions/formTemplates";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./FormTemplatePage.scss";

interface FormTemplatePageProps {
    form: Dtos.FormTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formTemplate: Dtos.FormTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingFormTemplate: boolean;
    loadFormTemplateFailure: boolean;
    loadFormTemplateErrorCode: string;
    savingFormTemplate: boolean;
    saveFormTemplateSuccess: boolean;
    saveFormTemplateFailure: boolean;
    creatingFormTemplate: boolean;
    createFormTemplateSuccess: boolean;
    validatingFormTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearFormTemplate: FormTemplateActions.IFormTemplateClearActionCreator;
    loadFormTemplate: FormTemplateActions.IFormTemplateLoadByIdActionCreator;
    saveFormTemplate: FormTemplateActions.IFormTemplateSaveActionCreator;
    createFormTemplate: FormTemplateActions.IFormTemplateCreateActionCreator;
    validateFormTemplate: FormTemplateActions.IFormTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.formTemplate";

const FormTemplatePage: FC<FormTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    formTemplate,
    collection,
    lookups,
    loadingFormTemplate,
    loadFormTemplateFailure,
    loadFormTemplateErrorCode,
    savingFormTemplate,
    saveFormTemplateSuccess,
    saveFormTemplateFailure,
    creatingFormTemplate,
    createFormTemplateSuccess,
    validatingFormTemplate,
    validationFailures,
    permissions,
    clearFormTemplate,
    loadFormTemplate,
    saveFormTemplate,
    createFormTemplate,
    validateFormTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { formTemplateId } = useParams<{ formTemplateId: string }>();
    const formTemplateIdNumber = parseInt(formTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.FormTemplate = {} as Dtos.FormTemplate;

        loadForm(reduxFormName, Object.assign({ ...formTemplate }, extraData));
    };


    // Load form template
    useEffect(() => {
        if (formTemplateIdNumber && !formTemplate && !loadingFormTemplate) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(FormTemplateBreadcrumbs(formTemplateIdNumber));
            loadFormTemplate(formTemplateIdNumber);
        } else if (
            !formTemplateIdNumber &&
            !creatingFormTemplate &&
            !createFormTemplateSuccess
        ) {
            createFormTemplate();
            setBreadcrumbs(FormTemplateCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (formTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        formTemplateIdNumber,
        formTemplate,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        FormTemplateBreadcrumbs,
        loadFormTemplate,
        createFormTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingFormTemplate,
        creatingFormTemplate,
        createFormTemplateSuccess,
        FormTemplateCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFormTemplateFailure) {
            navigate(`/error/${loadFormTemplateErrorCode}`);
        }
    }, [loadFormTemplateFailure, loadFormTemplateErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (formTemplateIdNumber && formTemplate?.name) {
            setBreadcrumbs(
                FormTemplateBreadcrumbs(
                    formTemplateIdNumber,
                    formTemplate?.name
                )
            );
        }
    }, [
        formTemplateIdNumber,
        formTemplate,
        setBreadcrumbs,
        FormTemplateBreadcrumbs,
    ]);

    // Reload form template after saving
    useEffect(() => {
        if (!savingFormTemplate && saveFormTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Form template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/formtemplates";
                navigate(url);
            } else if (!formTemplateIdNumber) {
                let url: string = `/settings/qa/formtemplates/${formTemplate?.id}`;
                navigate(url);
                clearFormTemplate();
            }
        }
    }, [
        savingFormTemplate,
        saveFormTemplateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingFormTemplate && saveFormTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingFormTemplate, saveFormTemplateFailure]);

    // Cleanup effect to clear the form template on component unmount
    useEffect(() => {
        return () => {
            clearFormTemplate();
            resetForm(reduxFormName);
        };
    }, [clearFormTemplate, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/formtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.FormTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.FormTemplate = {} as Dtos.FormTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            saveFormTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.FormTemplate = {} as Dtos.FormTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (form && hasBeenSubmitted && !validatingFormTemplate) {
                await validateFormTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.FormTemplatesUpdate,
                Dtos.Permission.FormTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingFormTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (hasPermission && form && !loadingFormTemplate) {
            return (
                <>
                    <br />
                    <h1>Components</h1>
                    <CollectionPage
                        qaItemComponents={true}
                        itemComponentTypeId={form.itemComponentTypeId || 0}
                        showSections={true}
                        readonly={formDisabled}
                        collectionId={form.collectionId || 0}
                    />
                </>
            );
        }
    }, [form, permissions, savingFormTemplate, formState, loadingFormTemplate]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.FormTemplatesUpdate,
                Dtos.Permission.FormTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingFormTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Form Template</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            description: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            itemComponentTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "FormTemplate",
                                                                "ItemComponentTypeId",
                                                                lookups
                                                            ),
                                                            form.itemComponentTypeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            formTemplateCategoryId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "FormTemplate",
                                                                "FormTemplateCategoryId",
                                                                lookups
                                                            ),
                                                            form.formTemplateCategoryId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "formTemplate",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "formtemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                </Form>
                <Form model={"reduxForms.collection"}>{renderCollection}</Form>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingFormTemplate,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        renderCollection,
    ]);

    return (
        <QALookupsLayout
            loading={loadingFormTemplate}
            permission={Dtos.Permission.FormTemplatesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<FormTemplatePageProps> => {
    let formTemplate: Dtos.FormTemplate | undefined = !(
        state.formTemplates.formData instanceof Array
    )
        ? state.formTemplates.formData
        : undefined;
    let collection: Dtos.Collection | undefined = !(
        state.reduxForms.collection instanceof Array
    )
        ? state.reduxForms.collection
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formTemplate: formTemplate!,
        collection: collection!,
        loadingFormTemplate:
            state.formTemplates.loadState &&
            state.formTemplates.loadState.status == RequestState.Pending,
        loadFormTemplateFailure:
            state.formTemplates.loadState &&
            state.formTemplates.loadState.status == RequestState.Failure,
        loadFormTemplateErrorCode: state.formTemplates.loadState.errorCode,
        savingFormTemplate:
            state.formTemplates.saveState &&
            state.formTemplates.saveState.status == RequestState.Pending,
        saveFormTemplateSuccess:
            state.formTemplates.saveState &&
            state.formTemplates.saveState.status == RequestState.Success,
        saveFormTemplateFailure:
            state.formTemplates.saveState &&
            state.formTemplates.saveState.status == RequestState.Failure,
        creatingFormTemplate:
            state.formTemplates.createState &&
            state.formTemplates.createState.status == RequestState.Pending,
        createFormTemplateSuccess:
            state.formTemplates.createState &&
            state.formTemplates.createState.status == RequestState.Success,
        validatingFormTemplate:
            state.formTemplates.validateState &&
            state.formTemplates.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.formTemplates.lookups,
        form: state.reduxForms.formTemplate,
        formState: state.formTemplates.formState,
        formProperties: state.formTemplates.formProperties,
        validationFailures: formTemplate
            ? state.formTemplates.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<FormTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadFormTemplate: bindActionCreators(
            FormTemplateActions.LoadFormTemplateById,
            dispatch
        ),
        clearFormTemplate: bindActionCreators(
            FormTemplateActions.Clear,
            dispatch
        ),
        saveFormTemplate: bindActionCreators(
            FormTemplateActions.SaveFormTemplate,
            dispatch
        ),
        createFormTemplate: bindActionCreators(
            FormTemplateActions.CreateFormTemplate,
            dispatch
        ),
        validateFormTemplate: bindActionCreators(
            FormTemplateActions.ValidateFormTemplate,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormTemplatePage);
